// Phase JB.3c — Founder Offer card.
//
// Auth-only. Visible only when:
//   - user is on the free tier (tier code is null OR not Pro/Sika-Pro),
//   - user has no existing active/accepted/redeemed offer,
//   - the global founder_offers_remaining() count > 0.
//
// Composes I0 primitives: Eyebrow + Stat + Badge + Countdown + Pill.
// No primitive edits.
//
// N06 honesty — globalRemaining is the real RPC value, never fudged.
// N07/N08 — no urgency vocab, no manipulative framing.

import { type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { Badge, Countdown, Eyebrow, Pill, Stat } from "../super-acca";
import { useAuthSession } from "../../auth/useAuthSession.ts";
import { useUserTier } from "../../auth/useUserTier.ts";
import {
  offerIsActive,
  useFounderOffer,
  type FounderOffer,
} from "../../conversion/useFounderOffer.ts";

const fontBody = "var(--sa-font-body)";
const fontMono = "var(--sa-font-mono)";

const wrapperStyle: CSSProperties = { padding: "12px 48px 12px" };
const cardStyle: CSSProperties = {
  border: "1px solid var(--sa-hairline)",
  background: "var(--sa-bg-panel)",
  borderRadius: 8,
  padding: 22,
  margin: "0 auto",
  maxWidth: 1180,
};

export interface FounderOfferCardProps {
  /** Test override: force user signed in. */
  forceUser?: { id: string } | null;
  /** Test override: tier_state. */
  tierOverride?: "free" | "pro" | null;
  /** Test override: offer row. */
  offerOverride?: FounderOffer | null;
  /** Test override: globalRemaining. */
  remainingOverride?: number | null;
}

export function FounderOfferCard({
  forceUser,
  tierOverride,
  offerOverride,
  remainingOverride,
}: FounderOfferCardProps = {}): JSX.Element | null {
  const live = useAuthSession();
  const liveTier = useUserTier();
  const liveOffer = useFounderOffer();

  const user = forceUser !== undefined ? forceUser : live.user;
  const tierCode = liveTier.tierCode;
  const isFree = tierOverride !== undefined
    ? tierOverride === "free"
    : (tierCode === null || tierCode === "free");

  const offer = offerOverride !== undefined ? offerOverride : liveOffer.offer;
  const globalRemaining = remainingOverride !== undefined
    ? remainingOverride
    : liveOffer.globalRemaining;

  if (!user) return null;
  if (!isFree) return null;

  // Active / accepted / redeemed → show the active state.
  if (offerIsActive(offer)) {
    if (!offer) return null;
    return (
      <section
        aria-label="Founder offer · active"
        data-testid="founder-offer-active"
        style={wrapperStyle}
      >
        <div style={cardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <Eyebrow>Founder window · 30% off forever</Eyebrow>
            <Badge variant="amber">CLAIMED</Badge>
          </div>
          <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
            <Stat label="Discount locked" value={`${offer.discount_pct}%`} accent />
            <div>
              <span style={{ fontFamily: fontMono, fontSize: 10.5, letterSpacing: "0.18em", color: "var(--sa-fg-muted)" }}>
                EXPIRES
              </span>
              <div style={{ marginTop: 4 }}>
                <Countdown targetISO={offer.expires_at} compact />
              </div>
            </div>
          </div>
          <p style={{ marginTop: 14, fontFamily: fontBody, fontSize: 13, color: "var(--sa-fg-secondary)", lineHeight: 1.7 }}>
            Your 30-day window is open. The discount stays valid forever once you activate Pro.
          </p>
          <div style={{ marginTop: 14 }}>
            <Link
              to="/pricing?founder=1"
              data-testid="founder-offer-activate-cta"
              style={{ textDecoration: "none" }}
            >
              <Pill color="var(--sa-accent-saffron)" borderColor="var(--sa-accent-saffron)">
                ACTIVATE PRO · 30% OFF
              </Pill>
            </Link>
          </div>
        </div>
      </section>
    );
  }

  // Cap reached → hide (the offer is genuinely closed).
  if (globalRemaining !== null && globalRemaining <= 0) return null;

  // Eligible-to-claim branch.
  return (
    <section
      aria-label="Founder offer"
      data-testid="founder-offer-eligible"
      style={wrapperStyle}
    >
      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <Eyebrow>30-day Founder window — 30% off forever, first 100 only</Eyebrow>
          <Badge variant="ghost">FOUNDER</Badge>
        </div>
        <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
          <Stat
            label="Spots remaining"
            value={globalRemaining === null ? "—" : String(globalRemaining)}
            accent
          />
          <Stat label="Discount" value="30%" />
        </div>
        <p style={{ marginTop: 14, fontFamily: fontBody, fontSize: 13.5, color: "var(--sa-fg-secondary)", lineHeight: 1.7 }}>
          The first 100 STRIKZA accounts get a 30% discount that stays valid forever. The number above is live — when it reaches zero the window closes.
        </p>
        {liveOffer.error !== null ? (
          <div data-testid="founder-offer-error" style={{ marginTop: 12, fontFamily: fontMono, fontSize: 11, color: "var(--sa-signal-red)" }}>
            {liveOffer.error}
          </div>
        ) : null}
        <div style={{ marginTop: 16 }}>
          <button
            type="button"
            onClick={() => void liveOffer.claim()}
            disabled={liveOffer.claiming}
            data-testid="founder-offer-claim-cta"
            style={{ background: "transparent", border: 0, padding: 0, cursor: liveOffer.claiming ? "default" : "pointer" }}
          >
            <Pill color="var(--sa-accent-saffron)" borderColor="var(--sa-accent-saffron)">
              {liveOffer.claiming ? "CLAIMING…" : "CLAIM FOUNDER WINDOW"}
            </Pill>
          </button>
        </div>
      </div>
    </section>
  );
}
